/**
 * Custom Validations (CV-5, LIQ-2272): the backend's checks on a rule's criteria, in JavaScript, so
 * the rule builder refuses what bdx-validation-service would.
 *
 * checkCriteria is criteria.py's validate (criteria format v1 - CRITERIA_FORMAT.md in lincoln,
 * criteria.py in bdx-validation-service) with the same messages and paths; criteria_examples_v1.json's
 * valid and invalid examples hold the two to that. columnsUsed is criteria.py's columns_used: the
 * rule's Columns.
 */

export const SCHEMA_VERSION = 1;
export const MAX_DEPTH = 12;

const CONDITION_TYPES = ["and", "or", "not", "if", "compare", "is_null", "is_not_null"];
const COMPARE_OPS = ["=", "!=", "<", "<=", ">", ">=", "in", "not_in", "starts_with", "ends_with", "contains"];
const ORDER_OPS = ["<", "<=", ">", ">="];
const TEXT_OPS = ["starts_with", "ends_with", "contains"];
const LIST_OPS = ["in", "not_in"];
export const ARITHMETIC_OPS = ["+", "-", "*", "/"];
const OPERAND_KEYS = ["column", "text", "number", "date", "boolean", "list"];

export const TEXT = "text";
export const NUMERIC = "numeric";
export const DATE = "date";
export const BOOLEAN = "boolean";

const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const isNumber = (value) => typeof value === "number" && Number.isFinite(value);

function isIsoDate(value) {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
	if (!match) {
		return false;
	}
	const [year, month, day] = match.slice(1).map(Number);
	const date = new Date(Date.UTC(year, month - 1, day));
	return year >= 1 && date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

const isList = (kind) => Boolean(kind) && kind.startsWith("list:");

/**
 * criteria.py's validate: every problem with a criteria document, as [{ code, message, path }] -
 * code invalid_criteria or unknown_column, message and path as the backend gives them. Empty when
 * the backend would accept it. With columnTypes, columns must exist and suit how they're used.
 */
export function checkCriteria(document, columnTypes = null) {
	const errors = [];
	const fail = (message, path, code = "invalid_criteria") => {
		errors.push({ code, message, path });
		return false;
	};
	// An operand that failed has no type, so it isn't reported again as a type mismatch.
	const invalid = (message, path, code) => {
		fail(message, path, code);
		return null;
	};
	const requireKeys = (node, path, required, optional = []) => {
		if (!isObject(node)) {
			return fail("must be a JSON object", path);
		}
		const keys = Object.keys(node);
		const missing = required.filter((key) => !keys.includes(key));
		if (missing.length) {
			return fail(`missing ${missing.map((key) => `'${key}'`).join(", ")}`, path);
		}
		const unexpected = keys.filter((key) => !required.includes(key) && !optional.includes(key)).sort();
		if (unexpected.length) {
			return fail(`unexpected ${unexpected.map((key) => `'${key}'`).join(", ")}`, path);
		}
		return true;
	};

	const sameType = (left, right, path) => {
		if (left && right && left !== right) {
			fail(`compares ${left} with ${right}`, path);
		}
	};

	const operand = (node, path, depth) => {
		if (depth > MAX_DEPTH) {
			return invalid(`nested more than ${MAX_DEPTH} levels deep`, path);
		}
		if (!isObject(node)) {
			return invalid("must be a JSON object", path);
		}
		if ("op" in node) {
			if (!requireKeys(node, path, ["op", "operands"])) {
				return null;
			}
			if (!ARITHMETIC_OPS.includes(node.op)) {
				fail(`unknown arithmetic op '${node.op}'; use one of: ${ARITHMETIC_OPS.join(", ")}`, `${path}.op`);
			}
			if (!Array.isArray(node.operands) || node.operands.length < 2) {
				fail("operands must be a list of at least 2", `${path}.operands`);
				return NUMERIC;
			}
			node.operands.forEach((item, index) => {
				const itemPath = `${path}.operands[${index}]`;
				const kind = operand(item, itemPath, depth + 1);
				if (kind && kind !== NUMERIC) {
					fail(`arithmetic needs numbers, not ${kind}`, itemPath);
				}
				if (node.op === "/" && index > 0 && isObject(item) && Object.keys(item).length === 1 && item.number === 0) {
					fail("division by zero", itemPath);
				}
			});
			return NUMERIC;
		}
		const keys = Object.keys(node);
		if (keys.length !== 1 || !OPERAND_KEYS.includes(keys[0])) {
			return invalid(`must have exactly one of: ${OPERAND_KEYS.join(", ")} - or op and operands`, path);
		}
		const [key, value] = Object.entries(node)[0];
		const valuePath = `${path}.${key}`;
		switch (key) {
			case "column":
				if (typeof value !== "string" || !value.trim()) {
					return invalid("column must be a column name", valuePath);
				}
				if (!columnTypes) {
					return null;
				}
				if (!(value in columnTypes)) {
					return invalid(`unknown column '${value}'`, valuePath, "unknown_column");
				}
				return columnTypes[value];
			case "text":
				if (typeof value !== "string" || !value.trim()) {
					fail("text must be a non-empty string; to test for a blank value use is_null", valuePath);
				}
				return TEXT;
			case "number":
				if (!isNumber(value)) {
					fail("number must be a number", valuePath);
				}
				return NUMERIC;
			case "date":
				if (typeof value !== "string" || value.length !== 10 || !isIsoDate(value)) {
					fail("date must be written YYYY-MM-DD", valuePath);
				}
				return DATE;
			case "boolean":
				if (typeof value !== "boolean") {
					fail("boolean must be true or false", valuePath);
				}
				return BOOLEAN;
			default:
				if (!Array.isArray(value) || !value.length) {
					return invalid("list must be a non-empty list", valuePath);
				}
				if (value.every((item) => typeof item === "string" && item.trim())) {
					return `list:${TEXT}`;
				}
				if (value.every(isNumber)) {
					return `list:${NUMERIC}`;
				}
				return invalid("list items must be all text or all numbers", valuePath);
		}
	};

	const compare = (node, path, depth) => {
		if (!requireKeys(node, path, ["type", "op", "left", "right"], ["tolerance", "whole_word"])) {
			return;
		}
		const { op } = node;
		if (!COMPARE_OPS.includes(op)) {
			fail(`unknown op '${op}'; use one of: ${COMPARE_OPS.join(", ")}`, `${path}.op`);
			return;
		}
		const left = operand(node.left, `${path}.left`, depth + 1);
		const right = operand(node.right, `${path}.right`, depth + 1);
		if (isList(left)) {
			fail("a list can only be on the right, with in or not_in", `${path}.left`);
		} else if (LIST_OPS.includes(op)) {
			if (!isList(right)) {
				fail(`${op} needs a list on the right`, `${path}.right`);
			} else {
				sameType(left, right.slice("list:".length), path);
			}
		} else if (isList(right)) {
			fail("a list can only be used with in or not_in", `${path}.right`);
		} else if (TEXT_OPS.includes(op)) {
			[["left", left], ["right", right]].forEach(([side, kind]) => {
				if (kind && kind !== TEXT) {
					fail(`${op} works on text, not ${kind}`, `${path}.${side}`);
				}
			});
		} else if (ORDER_OPS.includes(op)) {
			[["left", left], ["right", right]].forEach(([side, kind]) => {
				if (kind === TEXT || kind === BOOLEAN) {
					fail(`${op} works on numbers and dates, not ${kind}`, `${path}.${side}`);
				}
			});
			sameType(left, right, path);
		} else {
			sameType(left, right, path);
		}

		if ("tolerance" in node) {
			const { tolerance } = node;
			if (op !== "=" && op !== "!=") {
				fail("tolerance only applies to = and !=", `${path}.tolerance`);
			} else if (!isNumber(tolerance) || tolerance < 0) {
				fail("tolerance must be a number, 0 or more", `${path}.tolerance`);
			} else if ((left && left !== NUMERIC) || (right && right !== NUMERIC)) {
				fail("tolerance only applies to numbers", `${path}.tolerance`);
			}
		}
		if ("whole_word" in node) {
			if (op !== "contains") {
				fail("whole_word only applies to contains", `${path}.whole_word`);
			} else if (typeof node.whole_word !== "boolean") {
				fail("whole_word must be true or false", `${path}.whole_word`);
			}
		}
	};

	const condition = (node, path, depth) => {
		if (depth > MAX_DEPTH) {
			fail(`nested more than ${MAX_DEPTH} levels deep`, path);
			return;
		}
		if (!isObject(node) || !CONDITION_TYPES.includes(node.type)) {
			fail(`must be a condition whose type is one of: ${CONDITION_TYPES.join(", ")}`, path);
			return;
		}
		switch (node.type) {
			case "and":
			case "or":
				if (!requireKeys(node, path, ["type", "conditions"])) {
					return;
				}
				if (!Array.isArray(node.conditions) || !node.conditions.length) {
					fail("conditions must be a non-empty list", `${path}.conditions`);
					return;
				}
				node.conditions.forEach((item, index) => condition(item, `${path}.conditions[${index}]`, depth + 1));
				return;
			case "not":
				if (requireKeys(node, path, ["type", "condition"])) {
					condition(node.condition, `${path}.condition`, depth + 1);
				}
				return;
			case "if":
				if (requireKeys(node, path, ["type", "if", "then"])) {
					condition(node.if, `${path}.if`, depth + 1);
					condition(node.then, `${path}.then`, depth + 1);
				}
				return;
			case "is_null":
			case "is_not_null":
				if (!requireKeys(node, path, ["type", "operand"])) {
					return;
				}
				if (!isObject(node.operand) || !("column" in node.operand || "op" in node.operand)) {
					fail("must be a column or a calculation", `${path}.operand`);
					return;
				}
				operand(node.operand, `${path}.operand`, depth + 1);
				return;
			default:
				compare(node, path, depth);
		}
	};

	if (!requireKeys(document, "criteria", ["schema_version", "condition"])) {
		return errors;
	}
	if (document.schema_version !== SCHEMA_VERSION || !Number.isInteger(document.schema_version)) {
		fail(`schema_version must be ${SCHEMA_VERSION}`, "criteria.schema_version");
		return errors;
	}
	condition(document.condition, "condition", 1);
	return errors;
}

/** Every column the criteria read, in order of first appearance - the rule's Columns (criteria.py columns_used). */
export function columnsUsed(document) {
	const found = [];
	const walk = (node) => {
		if (Array.isArray(node)) {
			node.forEach(walk);
		} else if (isObject(node)) {
			const keys = Object.keys(node);
			if (keys.length === 1 && typeof node.column === "string") {
				if (!found.includes(node.column)) {
					found.push(node.column);
				}
				return;
			}
			Object.values(node).forEach(walk);
		}
	};
	if (document) {
		walk(document.condition);
	}
	return found;
}
